import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { DocumentViewer } from '../../components/DocumentViewer'
import {
  FileText,
  FilePdf,
  DownloadSimple,
  Eye,
  MagnifyingGlass,
  Spinner,
  Image as ImageIcon,
  File,
  Chat,
  CloudArrowUp,
  ArrowRight
} from '@phosphor-icons/react'
import { documentService, OrderDocumentWithOrder, DocumentCategory } from '../../services/documents'

export default function DocumentsPage() {
  const navigate = useNavigate()

  // State
  const [loading, setLoading] = useState(true)
  const [documents, setDocuments] = useState<OrderDocumentWithOrder[]>([])
  const [categories, setCategories] = useState<DocumentCategory[]>([])
  const [total, setTotal] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterCategory, setFilterCategory] = useState('all')
  const [filterSource, setFilterSource] = useState('all')

  // Document viewer state
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerDoc, setViewerDoc] = useState<{ url: string; name: string; type: string } | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    try {
      setLoading(true)
      const [docsResponse, catsResponse] = await Promise.all([
        documentService.getAllDocuments(100, 0),
        documentService.getCategories()
      ])
      setDocuments(docsResponse.documents)
      setTotal(docsResponse.total)
      setCategories(catsResponse.categories)
    } catch (error) {
      console.error('Error fetching documents:', error)
    } finally {
      setLoading(false)
    }
  }

  // Filter functionality
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.order_tx_control?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = filterCategory === 'all' || doc.category === filterCategory
    const matchesSource = filterSource === 'all' || doc.source === filterSource
    return matchesSearch && matchesCategory && matchesSource
  })

  function getCategoryLabel(value?: string): string {
    if (!value) return 'Nao classificado'
    const cat = categories.find(c => c.value === value)
    return cat?.label || value
  }

  function formatFileSize(bytes?: number): string {
    if (!bytes) return ''
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  function getDocumentIcon(doc: OrderDocumentWithOrder) {
    const mimeType = doc.mime_type || ''
    const lowerName = doc.name.toLowerCase()

    if (mimeType === 'application/pdf' || lowerName.endsWith('.pdf')) {
      return <FilePdf size={20} className="text-red-500" />
    }
    if (mimeType.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => lowerName.endsWith(ext))) {
      return <ImageIcon size={20} className="text-blue-500" />
    }
    return <File size={20} className="text-gray-400" />
  }

  function canPreview(doc: OrderDocumentWithOrder): boolean {
    const mimeType = doc.mime_type || ''
    const lowerName = doc.name.toLowerCase()

    if (mimeType === 'application/pdf' || lowerName.endsWith('.pdf')) return true
    if (mimeType.startsWith('image/') || ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => lowerName.endsWith(ext))) return true
    if (mimeType.includes('word') || mimeType.includes('document') || lowerName.endsWith('.doc') || lowerName.endsWith('.docx')) return true

    return false
  }

  function openViewer(doc: OrderDocumentWithOrder) {
    let mimeType = doc.mime_type || ''
    if (!mimeType) {
      const lowerName = doc.name.toLowerCase()
      if (lowerName.endsWith('.pdf')) mimeType = 'application/pdf'
      else if (lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg')) mimeType = 'image/jpeg'
      else if (lowerName.endsWith('.png')) mimeType = 'image/png'
      else if (lowerName.endsWith('.gif')) mimeType = 'image/gif'
      else if (lowerName.endsWith('.webp')) mimeType = 'image/webp'
      else if (lowerName.endsWith('.docx')) mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      else if (lowerName.endsWith('.doc')) mimeType = 'application/msword'
    }
    setViewerDoc({ url: doc.url, name: doc.name, type: mimeType })
    setViewerOpen(true)
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <Spinner size={32} className="animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-500">Carregando documentos...</span>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Documentos</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Visualize documentos de todos os seus pedidos
              </p>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-medium">{total}</span> documentos
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filter Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="flex-1 min-w-[250px] relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Buscar por nome ou numero do pedido..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">Todas as Categorias</option>
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            {/* Source Filter */}
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">Todas as Origens</option>
              <option value="upload">Upload</option>
              <option value="chat">Chat</option>
            </select>
          </div>
        </div>

        {/* Documents List */}
        <div className="bg-white rounded-lg border border-gray-200">
          {filteredDocuments.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {filteredDocuments.map((doc, idx) => (
                <div
                  key={doc.id || idx}
                  className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    {getDocumentIcon(doc)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900 truncate">{doc.name}</span>
                      {doc.source === 'chat' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                          <Chat size={12} />
                          Chat
                        </span>
                      ) : doc.source === 'upload' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                          <CloudArrowUp size={12} />
                          Upload
                        </span>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      {doc.category && (
                        <span className="bg-gray-100 px-2 py-0.5 rounded">
                          {getCategoryLabel(doc.category)}
                        </span>
                      )}
                      {doc.size && (
                        <span>{formatFileSize(doc.size)}</span>
                      )}
                      {doc.uploaded_at && (
                        <span>
                          {new Date(doc.uploaded_at).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                      {doc.uploaded_by_name && (
                        <span>por {doc.uploaded_by_name}</span>
                      )}
                    </div>
                    {doc.description && (
                      <p className="text-xs text-gray-600 mt-1 truncate">{doc.description}</p>
                    )}
                  </div>

                  {/* Order Link */}
                  <div className="hidden sm:block flex-shrink-0">
                    <button
                      onClick={() => navigate(`/shipper/orders/${doc.order_id}?tab=documents`)}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                    >
                      #{doc.order_tx_control}
                      <ArrowRight size={14} />
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {canPreview(doc) && (
                      <button
                        onClick={() => openViewer(doc)}
                        className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Visualizar"
                      >
                        <Eye size={18} />
                      </button>
                    )}
                    <a
                      href={doc.url}
                      download={doc.name}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Baixar"
                    >
                      <DownloadSimple size={18} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FileText size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-1">Nenhum documento encontrado</p>
              <p className="text-sm text-gray-400">
                Documentos enviados pelo chat ou upload aparecerao aqui
              </p>
            </div>
          )}

          {/* Footer with count */}
          {filteredDocuments.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-3 text-sm text-gray-600">
              Exibindo <span className="font-medium">{filteredDocuments.length}</span> de{' '}
              <span className="font-medium">{total}</span> documentos
            </div>
          )}
        </div>
      </main>

      {/* Document Viewer Modal */}
      {viewerDoc && (
        <DocumentViewer
          isOpen={viewerOpen}
          onClose={() => {
            setViewerOpen(false)
            setViewerDoc(null)
          }}
          url={viewerDoc.url}
          fileName={viewerDoc.name}
          mimeType={viewerDoc.type}
        />
      )}
    </AppLayout>
  )
}
